import React, { useEffect, useCallback } from "react";
import {
  StyleSheet,
  Pressable,
  View,
  TouchableOpacity,
  Image,
  FlatList,
} from "react-native";
import { useDispatch, useSelector } from "react-redux";
import LinearGradient from "react-native-linear-gradient";

//Custom Import
import color from "@constants/color";
import { scale } from "@utils/scale";
import Error from "@assets/svg/Error";
import t from "@constants/localization";
import BackArrow from "@assets/svg/BackArrow";
import RightArrow from "@assets/svg/RightArrow";
import CustomText from "@components/CustomText";
import contestAction from "@actions/contest.action";
import CircularLoader from "@screens/Story/CircularLoader";
import contestEvents, { CONTEST_TYPES } from "../ContestEvents";
import { CONTEST_EVENTS } from "@constants/analyticsEventsCT";
import { pastContestSelector } from "@selectors/contest.selector";
import { PastEmptyComponent } from "../Components/ContestEmptyComponent";

const PATH = "screens/Contest";

const PastContest = ({ navigation }) => {
  const dispatch = useDispatch();
  const { data = [], api = {} } = useSelector(pastContestSelector);
  const { fetched = false, pending = false } = api || {};

  useEffect(() => {
    dispatch(contestAction.getPastContest.pending());
  }, []);

  const onBackClick = () => {
    navigation.goBack();
  };

  const onPressContestItem = useCallback((item) => {
    const {
      contest_id,
      contest_name,
      start_time,
      end_time,
      contest_type,
      can_only_view,
      payout_time,
    } = item;
    contestEvents(CONTEST_EVENTS.CONTEST_CLICKED, {
      contest_id,
      state: "Past",
      title: contest_name,
      from_date: start_time,
      to_date: end_time,
      eligible: !can_only_view,
      contest_type: CONTEST_TYPES[contest_type],
      payout_sent: new Date() > new Date(payout_time) ? "Yes" : "No", //Yes/No (only after contest is ended)
    });
    navigation.navigate("ContestOverview", { contest_id, isPastContest: true });
  }, []);

  const _keyExtractor = (item, index) => `${item?.id}${index}`;

  const renderContestItem = ({ item }) => {
    const { contest_name, description, contest_id, end_time, can_only_view } =
      item;
    return (
      <Pressable
        onPress={() => onPressContestItem(item)}
        style={styles.cardContainer}
      >
        {item.contest_banner ? (
          <Image
            resizeMode={"stretch"}
            style={styles.liveBannerImage}
            source={{ uri: item.contest_banner }}
          />
        ) : (
          <LinearGradient
            colors={[color.darkSkyBlue, color.trans_209_248]}
            style={styles.bannerContainer}
            useAngle={true}
          >
            <CustomText
              variant="title_18"
              newfont={true}
              numberOfLines={3}
              style={styles.cardTitle}
            >
              {contest_name}
            </CustomText>
            <CustomText
              variant="caption_2"
              newfont={true}
              numberOfLines={4}
              style={styles.cardDescription}
            >
              {description}
            </CustomText>
            <View style={styles.contentContainer} />
            <Image
              source={require("../../../assets/images/contest-celebration.png")}
              style={styles.bannerImage}
            />
          </LinearGradient>
        )}
        {can_only_view ? (
          <View style={styles.eligibleContainer}>
            <Error />
            <CustomText
              variant="subtitle_16"
              newfont={true}
              numberOfLines={1}
              style={styles.eligibleText}
            >
              {t(`${PATH}/contestDetails/notEligible`)}
            </CustomText>
          </View>
        ) : null}
        <View style={styles.cardBottomStrip}>
          <CustomText
            variant="subtitle_16"
            newfont={true}
            numberOfLines={1}
            style={styles.stripDate}
          >
            {t(`${PATH}/seeContest`)}
          </CustomText>
          <RightArrow
            fill={color.modalBackground}
            stroke={color.modalBackground}
          />
        </View>
      </Pressable>
    );
  };

  return (
    <React.Fragment>
      <View style={styles.headerContainer}>
        <View style={styles.headerTitleContainer}>
          <TouchableOpacity activeOpacity={0.8} onPress={onBackClick}>
            <BackArrow />
          </TouchableOpacity>
          <CustomText
            variant="title_20"
            newfont={true}
            numberOfLines={1}
            style={styles.textLeftPadding}
          >
            {t(`${PATH}/pastContest`)}
          </CustomText>
        </View>
      </View>
      <View style={styles.container}>
        {!fetched && pending ? (
          <View style={styles.loaderStyle}>
            <CircularLoader />
          </View>
        ) : (
          <FlatList
            removeClippedSubviews
            data={data}
            keyExtractor={_keyExtractor}
            renderItem={renderContestItem}
            contentContainerStyle={styles.listContainer}
            ListEmptyComponent={PastEmptyComponent}
            style={styles.mainListContainer}
            showsVerticalScrollIndicator={false}
            showsHorizontalScrollIndicator={false}
          />
        )}
      </View>
    </React.Fragment>
  );
};

export default PastContest;

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  mainListContainer: {
    flex: 1,
  },
  listContainer: {
    paddingHorizontal: 16,
    backgroundColor: color.white,
    flexGrow: 1,
    paddingTop: 10,
  },
  headerContainer: {
    flexDirection: "row",
    height: 56,
    paddingHorizontal: scale(16),
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: color.white,
    elevation: 8,
    shadowColor: color.mediumBlack,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4.65,
  },
  headerTitleContainer: {
    alignItems: "center",
    flexDirection: "row",
  },
  textLeftPadding: {
    paddingLeft: scale(23),
    color: color.mediumBlack,
  },
  sectionIcon: {
    width: 16,
    height: 16,
  },
  cardContainer: {
    backgroundColor: color.white,
    elevation: 8,
    shadowColor: color.mediumBlack,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4.65,
    marginBottom: 12,
    borderRadius: scale(10),
    overflow: "hidden",
  },
  bannerContainer: {
    height: 167,
    borderTopLeftRadius: scale(10),
    borderTopRightRadius: scale(10),
    flexDirection: "column",
    paddingHorizontal: 16,
  },
  bannerImage: {
    width: 174,
    height: 163,
    position: "absolute",
    end: 0,
    alignSelf: "center",
  },
  cardTitle: {
    color: color.mediumBlack,
    maxWidth: scale(161),
    lineHeight: 24,
    marginTop: 30,
  },
  cardDescription: {
    color: color.mediumBlack,
    maxWidth: scale(161),
    marginTop: 26,
  },
  cardBottomStrip: {
    height: 44,
    paddingHorizontal: 16,
    paddingVertical: 10,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderTopWidth: 1,
    borderColor: color.blank_grey,
  },
  stripDate: {
    color: color.mediumBlack,
  },
  liveBannerImage: {
    height: 167,
    backgroundColor: color.white,
  },
  loaderStyle: {
    position: "absolute",
    alignSelf: "center",
    zIndex: 100,
  },
  eligibleContainer: {
    flexDirection: "row",
    paddingHorizontal: 16,
    marginVertical: 10,
    alignContent: "center",
  },
  eligibleText: {
    color: color.mediumBlack,
    marginStart: 11,
  },
});
